import "./styles/layers.css";
import "@mantine/core/styles.layer.css";
import "@mantine/notifications/styles.layer.css";
import "@mantine/dates/styles.layer.css";
import "@xplatform/design-system/styles.layer.css";
import i18n, { I18N_NAMESPACE } from "./config/i18n";
import "./index.scss";
import type React from "react";
import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Notifications } from "@mantine/notifications";
import { XPlatformProvider } from "@xplatform/design-system";
import { injectStylesheet } from "@utils/injectStyles";
import DashboardPage from "@pages/DashboardPage/DashboardPage";
import MappingServicePage from "@pages/MappingServicePage/MappingServicePage";
import JobResultsPage from "@pages/JobResultsPage/JobResultsPage";
import { ContextProvider } from "./ContextProvider";
import { I18nextProvider } from "react-i18next";

/**
 * Root component exposed via Module Federation as `./App`. The
 * shell mounts this under its own `BrowserRouter`, and when run
 * standalone (local dev) `main.tsx` wraps it in a router.
 *
 * Routes:
 *   `/`                                     redirect to `/dashboard`
 *   `/dashboard`                            the mapping-services card grid
 *   `/services/:serviceId`                  jobs table + upload
 *   `/services/:serviceId/jobs/:jobId`      per-job result editor
 */
const MappingApp: React.FC = () => {
  // The imports above only reach the DOM when this module is served
  // standalone; under federation the emitted assets/style.css is never
  // linked, so inject it from this module's own base URL. Do not rely on the
  // shell for this — it resolves module stylesheets per Vite mode and got
  // sandbox wrong, which left this module unstyled there.
  useEffect(() => {
    const base = import.meta.env.BASE_URL || "/";
    injectStylesheet(`${base}assets/style.css`);
  }, []);

  return (
    // The module provides its own XPlatformProvider so its Mantine + design-system
    // components always resolve a MantineProvider from the SAME @mantine/core
    // instance they are bundled with — independent of the federation host's
    // shared scope. Nested harmlessly under the shell's own provider.
    // Scopes every bare useTranslation() in this module to the
    // `mapping` namespace. Module keys can no longer collide with
    // another module's in the shared `translation` namespace, which
    // the shell and BeeHive keep using.
    <I18nextProvider i18n={i18n} defaultNS={I18N_NAMESPACE}>
      <XPlatformProvider>
        <ContextProvider>
          <Notifications position="bottom-right" autoClose={5000} />
          <Routes>
            <Route path="/" element={<Navigate to="dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/services/:serviceId" element={<MappingServicePage />} />
            <Route
              path="/services/:serviceId/jobs/:jobId"
              element={<JobResultsPage />}
            />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </ContextProvider>
      </XPlatformProvider>
    </I18nextProvider>
  );
};

export default MappingApp;
